import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';

// services
import NotificationService from 'core/services/notification';
import { onDeleteScreen, onGetScreens, onUpdateScreen } from '../../service/splashScreen';
import authService from 'core/services/auth';

// models
import { SplashScreen } from '../../models/splashScreen';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from '../../../../components/loadingComponent/loadingComponent';
import ScreenItemComponent from 'modules/splashScreen/components/item/item';

//styles
import styles from './list.module.scss';
import AuthActionComponent from 'components/authAction/authAction';

function SplashScreenListPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [screens, setScreens] = useState<SplashScreen[]>([]);
	const [isReorder, setIsReorder] = useState(false);
	const [reorderLoading, setReorderLoading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedScreen, setTempDeletedScreen] = useState<number>();

	useEffect(() => {
		getScreens();
	}, []);

	const getScreens = async () => {
		try {
			setIsLoading(true);

			const response = await onGetScreens();
			const screens = response.data.data.mobile_app_intro_sliders;
			setScreens(screens.sort((a, b) => a.sort - b.sort));

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 250,
				tolerance: 5,
			},
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (!over || active.id === over.id) return;

		const oldIndex = screens.findIndex((item) => item.id === active.id);
		const newIndex = screens.findIndex((item) => item.id === over.id);

		const newItems = arrayMove(screens, oldIndex, newIndex);

		newItems.forEach((item, index) => {
			item.sort = index + 1;
		});

		setScreens(newItems);
	};

	const onShowConfirmation = (screenId: number) => {
		setShowConfirmation(true);
		setTempDeletedScreen(screenId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteScreen(tempDeletedScreen as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('SPLASH_SCREENS.DELETED'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getScreens();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.ERROR'), t('SPLASH_SCREENS.DELETE_ERROR'));
		}
	};

	const onChangOrder = async () => {
		if (isReorder) {
			setReorderLoading(true);

			try {
				const requests: Promise<any>[] = [];

				screens.forEach((el) => {
					const formData = new FormData();
					formData.append('_method', 'put');
					formData.append('sort', `${el.sort}`);
					requests.push(onUpdateScreen(el.id, formData));
				});

				await Promise.all(requests);
				setReorderLoading(false);
				setIsReorder(false);
				NotificationService.success(t('COMMON.SUCCESS'), t('SPLASH_SCREENS.SORTED'));
			} catch (error) {
				setReorderLoading(false);
				NotificationService.error(t('COMMON.ERROR'), t('SPLASH_SCREENS.SORT_ERROR'));
			}
		} else {
			setIsReorder(!isReorder);
		}
	};

	return (
		<>
			<div className="pageHeader multiple">
				{/* <h3>{t('SPLASH_SCREENS.TITLE')}</h3> */}
				{!isLoading && authService && (
					<div className={styles.splash_actions}>
						<AuthActionComponent action="mobile_app_intro_slider_update">
							<Button type="primary" ghost={true} onClick={onChangOrder} loading={reorderLoading}>
								{isReorder ? t('COMMON.CONFIRM') : t('SPLASH_SCREENS.REARRANGE')}
							</Button>
						</AuthActionComponent>
						<AuthActionComponent action="mobile_app_intro_slider_create">
							<Link to="/splash-screens/create">
								<Button type="primary" className=" d-block w-100">
									{t('SPLASH_SCREENS.ADD_SCREEN')}
								</Button>
							</Link>
						</AuthActionComponent>
					</div>
				)}
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="mt-3">
					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
						<SortableContext items={screens.map((screen) => screen.id)} strategy={verticalListSortingStrategy} disabled={reorderLoading}>
							{screens.map((screen) => (
								<ScreenItemComponent introItem={screen} key={screen.id} isReorder={isReorder} onDelete={onShowConfirmation} />
							))}
						</SortableContext>
					</DndContext>
				</div>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				title={t('SPLASH_SCREENS.DELETE')}
				confirmText={t('COMMON.DELETE')}
				description={t('SPLASH_SCREENS.DELETE_MODAL_DESCRIPTION')}
				note={t('SPLASH_SCREENS.DELETE_MODAL_NOTE')}
				onDiscard={onDiscardConfirmation}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default SplashScreenListPage;
